"use client"

// Globaler Error-Boundary. In Production gibt Next.js nur `error.digest`
// (Message redacted). Wir können daher 'Server-Action-Hash-Drift' nicht
// aus error.message ablesen — stattdessen prüfen wir aktiv den
// Build-Hash gegen den initial gesehenen aus BuildHeartbeat.
//
// Strategie:
//   1. Bei jedem Crash: fetch /api/build-info
//   2. Vergleich mit sessionStorage.rvs.initial_build_id
//   3. Drift erkannt → auto-reload nach 1.5s
//   4. Kein Drift → echter Bug, User-Action mit Reset/Reload-Buttons

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { RefreshCw, ArrowLeft } from "lucide-react"

const INITIAL_BUILD_KEY = "rvs.initial_build_id"

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [diagnosis, setDiagnosis] = useState<
    "unknown" | "build_drift" | "real_bug"
  >("unknown")

  useEffect(() => {
    let cancelled = false
    async function diagnose() {
      let stored: string | null = null
      try {
        stored = sessionStorage.getItem(INITIAL_BUILD_KEY)
      } catch {
        // ignore
      }
      if (!stored) {
        // Wir wissen nichts ueber den Initial-Build, kein Drift-Check
        // möglich. Lass es als "unknown" → User-Action.
        if (!cancelled) setDiagnosis("real_bug")
        return
      }
      try {
        const res = await fetch("/api/build-info", {
          cache: "no-store",
          credentials: "include",
        })
        if (!res.ok) {
          if (!cancelled) setDiagnosis("real_bug")
          return
        }
        const data = (await res.json()) as { build_id?: string }
        if (cancelled) return
        if (data.build_id && data.build_id !== stored) {
          setDiagnosis("build_drift")
        } else {
          setDiagnosis("real_bug")
        }
      } catch {
        if (!cancelled) setDiagnosis("real_bug")
      }
    }
    diagnose()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (diagnosis !== "build_drift") return
    const t = setTimeout(() => {
      try {
        sessionStorage.removeItem(INITIAL_BUILD_KEY)
      } catch {
        // ignore
      }
      window.location.reload()
    }, 1500)
    return () => clearTimeout(t)
  }, [diagnosis])

  return (
    <html lang="de">
      <body>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full rounded-lg border bg-card p-6 space-y-4 text-center">
            <div className="text-4xl">
              {diagnosis === "build_drift" ? "🔄" : "⚠️"}
            </div>
            <h1 className="text-xl font-semibold">
              {diagnosis === "build_drift"
                ? "System wurde gerade aktualisiert"
                : diagnosis === "unknown"
                  ? "Lade Fehler-Diagnose…"
                  : "Etwas ist schiefgelaufen"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {diagnosis === "build_drift"
                ? "Die Seite wird gleich neu geladen, damit du die neueste Version nutzen kannst."
                : diagnosis === "unknown"
                  ? "Prüfe gerade ob das System gerade ein Update gemacht hat…"
                  : "Bitte die Seite neu laden. Falls das Problem bestehen bleibt, ist es kurzzeitig ein Server-Problem."}
            </p>
            {error?.digest && diagnosis !== "build_drift" && (
              <p className="text-xs text-muted-foreground font-mono">
                Fehler-ID: {error.digest}
              </p>
            )}
            {diagnosis !== "unknown" && (
              <div className="flex items-center justify-center gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.history.back()}
                >
                  <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
                  Zurück
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    if (diagnosis === "build_drift") {
                      try {
                        sessionStorage.removeItem(INITIAL_BUILD_KEY)
                      } catch {
                        // ignore
                      }
                      window.location.reload()
                    } else {
                      reset()
                    }
                  }}
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  {diagnosis === "build_drift"
                    ? "Jetzt neu laden"
                    : "Erneut versuchen"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </body>
    </html>
  )
}
